import { z } from "zod";
/**
 * The Iranian national identifier (کد ملی), and whether one is real.
 *
 * In `@talent/protocol` rather than in the API, because both sides need the same answer: the server
 * must refuse an invalid one, and the registration form should say so before a teacher submits and
 * waits. Two implementations of a checksum is one implementation and one thing that disagrees with it.
 *
 * **A valid checksum is not a real person.** It proves the number is well-formed — that somebody typed
 * ten digits rather than nine, and did not transpose two of them. It proves nothing about identity, and
 * nothing here treats it as proof: the product collects it because a preschool's records are keyed on
 * it, not to authenticate anybody. Authentication is the SMS code and only that (ADR-006).
 */
export const NATIONAL_ID_DIGITS = 10;
/**
 * Persian and Arabic-Indic digits folded to Latin.
 *
 * Duplicated from the web app's `format.ts` on purpose: this package has no dependency on the client,
 * and the server must be able to accept a number a Persian keyboard produced without the browser having
 * normalised it first. A rule enforced only on the client is not enforced.
 */
export function toLatinDigits(input) {
    return input
        .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
        .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660));
}
/**
 * Whether a national identifier passes its own check digit.
 *
 * The rule, which is a weighted modulus-11 check:
 *
 *   1. Ten digits, after folding Persian numerals and dropping separators. Fewer is not a national id;
 *      more is a typo. Leading zeros are significant, so this is a string throughout — parsing it as a
 *      number loses them and turns `0067749828` into a nine-digit value that then fails.
 *   2. All ten digits the same is refused. `1111111111` and its nine siblings satisfy the arithmetic and
 *      are not issued to anybody, so they are the placeholder somebody types to get past a form.
 *   3. Weight the first nine digits by 10 down to 2, sum, and take modulus 11. A remainder under 2 must
 *      equal the tenth digit; otherwise the tenth digit must be 11 minus the remainder.
 */
export function isValidNationalId(input) {
    const digits = toLatinDigits(input).replace(/[^0-9]/g, "");
    if (digits.length !== NATIONAL_ID_DIGITS)
        return false;
    if (/^(\d)\1{9}$/.test(digits))
        return false;
    const check = Number(digits[9]);
    let sum = 0;
    for (let i = 0; i < 9; i++) {
        sum += Number(digits[i]) * (10 - i);
    }
    const remainder = sum % 11;
    return remainder < 2 ? check === remainder : check === 11 - remainder;
}
/** Ten Latin digits, whatever was typed. The form a national id is stored in. */
export function normaliseNationalId(input) {
    return toLatinDigits(input).replace(/[^0-9]/g, "");
}
/**
 * A national id, as a field on a request.
 *
 * Normalises before checking, so a number typed with Persian digits or spaced into groups is accepted
 * rather than refused for its formatting — and stores the normalised form, so two spellings of the same
 * id cannot become two teachers.
 */
export const NationalIdSchema = z
    .string()
    .transform(normaliseNationalId)
    .refine(isValidNationalId, { message: "national id fails its check digit" });
//# sourceMappingURL=national-id.js.map